const APP_HEIGHT_VAR = '--app-height'
const SHRINK_IGNORE_PX = 40
const KEYBOARD_CLOSED_PX = 24
const KEYBOARD_WAIT_MS = 320

function readLiveViewportHeight() {
  const visual = window.visualViewport?.height
  return Math.round(Math.max(window.innerHeight, visual ?? 0))
}

function readLockedAppHeight() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(APP_HEIGHT_VAR)
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : readLiveViewportHeight()
}

function writeAppHeight(height: number) {
  document.documentElement.style.setProperty(APP_HEIGHT_VAR, `${height}px`)
}

function enableVirtualKeyboardOverlay() {
  const keyboard = (navigator as Navigator & { virtualKeyboard?: { overlaysContent: boolean } })
    .virtualKeyboard
  if (!keyboard) return
  keyboard.overlaysContent = true
}

export function getKeyboardOverlapPx() {
  return Math.max(0, readLockedAppHeight() - readLiveViewportHeight())
}

/**
 * Keep the map shell at the keyboard-closed height so the IME overlays the
 * bottom instead of shrinking `100vh` and dragging floating controls up.
 * Height shrinks are ignored unless the orientation changes.
 */
export function lockWebviewLayout() {
  enableVirtualKeyboardOverlay()

  const apply = (fromOrientation: boolean) => {
    const live = readLiveViewportHeight()
    if (live < 1) return

    const locked = readLockedAppHeight()
    const shrunk = live < locked - SHRINK_IGNORE_PX
    if (!fromOrientation && locked > 0 && shrunk) return

    writeAppHeight(live)
  }

  apply(false)

  const handleOrientation = () => {
    window.setTimeout(() => apply(true), 350)
  }

  window.addEventListener('orientationchange', handleOrientation)
  window.addEventListener('resize', () => apply(false))
  window.visualViewport?.addEventListener('resize', () => apply(false))
}

export function waitForKeyboardToClose(timeoutMs = KEYBOARD_WAIT_MS) {
  return new Promise<void>((resolve) => {
    if (getKeyboardOverlapPx() < KEYBOARD_CLOSED_PX) {
      resolve()
      return
    }

    const startedAt = performance.now()
    let timeoutId = 0

    const finish = () => {
      window.clearTimeout(timeoutId)
      window.removeEventListener('resize', onChange)
      window.visualViewport?.removeEventListener('resize', onChange)
      resolve()
    }

    const onChange = () => {
      const closed = getKeyboardOverlapPx() < KEYBOARD_CLOSED_PX
      const timedOut = performance.now() - startedAt >= timeoutMs
      if (closed || timedOut) finish()
    }

    window.addEventListener('resize', onChange)
    window.visualViewport?.addEventListener('resize', onChange)
    timeoutId = window.setTimeout(onChange, timeoutMs)
  })
}
